import { useState, useEffect, useRef } from 'react';
import { Pencil, Trash2, Camera, Package2 } from 'lucide-react';
import { useConfirm } from '../../components/boothly/confirm-provider.jsx';
import { SheetShell } from '../../components/boothly/sheet-shell.jsx';
import { InsetGroup, InsetRow } from '../../components/boothly/inset-list.jsx';
import { fmtCurrency } from '../../lib/format.js';

export function ItemDetailSheet({ item, open, onOpenChange, onUpdateItem, onUploadItemPhoto, onDeleteItem, getPhotoUrl, dark, categories }) {
  const confirm = useConfirm();
  const [editMode, setEditMode] = useState(false);
  const [editName, setEditName] = useState('');
  const [editPrice, setEditPrice] = useState('');
  const [editCategory, setEditCategory] = useState('');
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (open && item) {
      setEditMode(false);
      setEditName(item.name || '');
      setEditPrice(item.wish_price != null ? String(item.wish_price) : item.price != null ? String(item.price) : '');
      setEditCategory(item.metadata?.category ?? item.category ?? '');
      setPhotoFile(null);
      setPhotoPreview(null);
    }
  }, [open, item]);

  if (!open || !item) return null;

  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setPhotoPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const statusConfig = {
    verfuegbar: { label: 'Verfügbar', cls: 'text-[var(--label-secondary)]' },
    reserviert: { label: 'Reserviert', cls: 'rounded-[6px] bg-[var(--tint-yellow)] px-1.5 py-px font-semibold text-[var(--label-primary)]' },
    verkauft: { label: 'Verkauft', cls: 'rounded-[6px] bg-[var(--tint-stand)] px-1.5 py-px font-semibold text-[var(--accent-stand)]' },
  };
  const status = statusConfig[item.status] ?? statusConfig.verfuegbar;
  const baseImg = (getPhotoUrl && item.image_key) ? getPhotoUrl(item.image_key) : (item.image || null);
  const wish = item.wish_price != null ? item.wish_price : item.price;
  const imgSrc = baseImg ? (item._photoV ? `${baseImg}?v=${item._photoV}` : baseImg) : null;

  const allCategories = categories && categories.length > 0
    ? categories
    : ['Vintage', 'Bücher', 'Deko', 'Kleidung', 'Elektronik', 'Sonstiges'];

  const handleSave = async () => {
    const price = parseFloat(editPrice);
    onUpdateItem?.(item.id, {
      name: editName,
      wish_price: isNaN(price) ? null : price,
      price: isNaN(price) ? null : price,
      metadata: { ...(item.metadata || {}), category: editCategory },
    });
    if (photoFile && onUploadItemPhoto) {
      try {
        const { compressImage } = await import('../../photo-utils.js');
        const blob = await compressImage(photoFile);
        await onUploadItemPhoto(item.id, blob);
      } catch (_e) { /* Foto-Fehler non-fatal, Felder sind gespeichert */ }
    }
    setEditMode(false);
    onOpenChange(false);
  };

  const handleDelete = async () => {
    const ok = await confirm({
      title: 'Item entfernen?',
      subtitle: `„${item.name}" wird aus dem Sortiment entfernt.`,
      confirmLabel: 'Entfernen',
      destructive: true,
    });
    if (!ok) return;
    onDeleteItem?.(item.id);
    onOpenChange(false);
  };

  const close = () => onOpenChange(false);

  if (editMode) {
    return (
      <SheetShell
        open={open}
        onClose={close}
        grouped
        testId="item-edit-sheet"
        title="Artikel bearbeiten"
        footer={
          <div className="flex gap-3">
            <button
              onClick={() => setEditMode(false)}
              className="h-[50px] flex-1 rounded-[14px] bg-[var(--fill-tertiary)] text-[17px] font-semibold active:scale-[0.98]"
              style={{ transition: 'transform 100ms var(--ease-out-ios)' }}
            >
              Abbrechen
            </button>
            <button
              onClick={handleSave}
              disabled={!editName}
              className="h-[50px] flex-1 rounded-[14px] bg-[var(--accent-troedel)] text-[17px] font-semibold text-white shadow-[var(--shine-button)] active:scale-[0.98] disabled:opacity-40"
              style={{ transition: 'transform 100ms var(--ease-out-ios)' }}
            >
              Speichern
            </button>
          </div>
        }
      >
        <div className="space-y-6 pb-4 pt-1">
          {/* Foto hinzufügen/ersetzen */}
          <div>
            <input ref={fileInputRef} type="file" accept="image/*" capture="environment" onChange={handlePhotoChange} className="hidden" />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="relative flex h-40 w-full items-center justify-center overflow-hidden rounded-[16px] bg-[var(--bg-L1)] active:opacity-80 dark:bg-[var(--bg-L2)]"
            >
              {(photoPreview || imgSrc) ? (
                <>
                  <img src={photoPreview || imgSrc} alt="Vorschau" className="h-full w-full object-cover" />
                  <span className="absolute bottom-2 right-2 flex h-9 items-center gap-1.5 rounded-full bg-black/55 px-3 text-[13px] font-semibold text-white" style={{ WebkitBackdropFilter: 'blur(12px)', backdropFilter: 'blur(12px)' }}>
                    <Camera className="h-4 w-4" /> Ersetzen
                  </span>
                </>
              ) : (
                <span className="flex flex-col items-center gap-1.5 text-[var(--accent-troedel)]">
                  <Camera className="h-7 w-7" />
                  <span className="text-[15px] font-semibold">Foto aufnehmen</span>
                  <span className="text-[13px] text-[var(--label-secondary)]">oder aus der Mediathek wählen</span>
                </span>
              )}
            </button>
          </div>

          <InsetGroup surface="L2">
            <label className="flex min-h-[52px] items-center gap-3 px-4">
              <span className="w-28 shrink-0 text-[17px]">Name</span>
              <input
                type="text"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                enterKeyHint="next"
                className="min-w-0 flex-1 bg-transparent text-right text-[17px] text-[var(--label-secondary)] outline-none focus:text-[var(--label-primary)]"
              />
            </label>
            <label className="flex min-h-[52px] items-center gap-3 border-t border-[var(--separator)] pl-0 pr-4 [margin-left:16px]">
              <span className="w-28 shrink-0 text-[17px]">Wunschpreis</span>
              <input
                type="number"
                inputMode="decimal"
                value={editPrice}
                onChange={(e) => setEditPrice(e.target.value)}
                placeholder="0"
                className="num no-spin min-w-0 flex-1 bg-transparent text-right text-[17px] font-semibold outline-none"
              />
            </label>
          </InsetGroup>

          <InsetGroup title="Kategorie" surface="L2">
            <div className="flex flex-wrap gap-2 p-3">
              {allCategories.map(cat => {
                const active = editCategory === cat;
                return (
                  <button
                    key={cat}
                    onClick={() => setEditCategory(cat)}
                    aria-pressed={active}
                    className={`h-11 rounded-full px-4 text-[15px] font-semibold ${
                      active ? 'bg-[var(--accent-troedel)] text-white' : 'bg-[var(--fill-tertiary)] text-[var(--label-primary)]'
                    }`}
                    style={{ transition: 'background-color 150ms var(--ease-out-ios), color 150ms' }}
                  >
                    {cat}
                  </button>
                );
              })}
            </div>
          </InsetGroup>
        </div>
      </SheetShell>
    );
  }

  return (
    <SheetShell
      open={open}
      onClose={close}
      grouped
      testId="item-detail-sheet"
      title={item.name}
      subtitle={<span className="num">{wish != null ? fmtCurrency(wish) : ''}</span>}
      footer={item.status !== 'verkauft' && (
        <button
          onClick={() => setEditMode(true)}
          className="flex h-[50px] w-full items-center justify-center gap-2 rounded-[14px] bg-[var(--accent-troedel)] text-[17px] font-semibold text-white shadow-[var(--shine-button)] active:scale-[0.98]"
          style={{ transition: 'transform 100ms var(--ease-out-ios)' }}
        >
          <Pencil className="h-[18px] w-[18px]" />
          Bearbeiten
        </button>
      )}
    >
      <div className="space-y-6 pb-4 pt-1">
        <div className="aspect-[4/3] overflow-hidden rounded-[16px] bg-[var(--bg-L1)] dark:bg-[var(--bg-L2)]">
          {imgSrc ? (
            <img src={imgSrc} alt={item.name} className="h-full w-full object-cover" />
          ) : (
            <button
              type="button"
              onClick={() => setEditMode(true)}
              disabled={item.status === 'verkauft'}
              className="flex h-full w-full flex-col items-center justify-center gap-2 text-[var(--label-secondary)]"
            >
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-[var(--tint-troedel)]">
                <Package2 className="h-7 w-7 text-[var(--accent-troedel)]" />
              </span>
              <span className="text-[15px]">Kein Foto</span>
            </button>
          )}
        </div>

        <InsetGroup surface="L2">
          <InsetRow first title="Wunschpreis" value={wish != null ? fmtCurrency(wish) : '\u2014'} valueClassName="font-semibold" />
          <InsetRow title="Kategorie" value={<span className="text-[var(--label-secondary)]">{item.metadata?.category ?? item.category ?? '\u2014'}</span>} />
          <InsetRow title="Status" value={<span className={`text-[15px] ${status.cls}`}>{status.label}</span>} />
          {item.status === 'verkauft' && (
            <InsetRow
              title="Verkauft für"
              value={item.soldInfo || fmtCurrency(item.soldPrice ?? item.price ?? 0)}
            />
          )}
        </InsetGroup>

        {item.status !== 'verkauft' && (
          <InsetGroup surface="L2">
            <button
              type="button"
              onClick={handleDelete}
              className="flex min-h-[52px] w-full items-center justify-center gap-2 text-[17px] text-[var(--accent-red)] active:bg-[var(--fill-quaternary)]"
            >
              <Trash2 className="h-[18px] w-[18px]" />
              Aus dem Sortiment entfernen
            </button>
          </InsetGroup>
        )}
      </div>
    </SheetShell>
  );
}
